import Moment from 'moment';
import path from 'path';
import React, { useEffect, useRef, useState } from "react";
import {
    Badge, Button, ButtonGroup, Dropdown, DropdownDivider, DropdownItem, DropdownMenu,
    DropdownToggle, FormCheck, FormControl, InputGroup, Modal, ModalBody, ModalFooter,
    ModalHeader, Offcanvas, OffcanvasBody, OffcanvasHeader, OffcanvasTitle, Overlay,
    OverlayTrigger, Popover, PopoverBody, PopoverHeader, Tooltip,
} from "react-bootstrap";
import Scrollbars from "react-custom-scrollbars";
import { ClipLoader, FadeLoader } from "react-spinners";
import SAEmpty from "../../components/SAEmpty.js";
import { showNotificationGlobal as _showNotificationGlobal } from "../../components/common/NotificationProvider";
import StorageFiles from '../../components/storage/StorageFiles.js';
import FileJobsService from "../../services/api/FileJobs/index.js";
import StorageService from '../../services/api/Storage/index.js';
import StorageConfigService from "../../services/api/storageConfig/index.js";
import {
    checkZipExtension, duplicateFilePath, fileSizeSI, findIcon,
    generateTinyUrl, imageFileExtension, uuidv4,
} from "../../settings/shared/commonMethods.js";
import {
    API_RESPONSE, CALLBACKTIMEOUT, folderRegex, folderNameWithSpaceRegex, MAX_COPY_MOVE_FILES, MAX_DOWNLOAD_FILES,
    STORAGE_DEFAULT_MOUNT_PATH, STORAGE_EXTERNAL_MOUNT_PATH, websocketAction,
} from "../../settings/shared/constants.js";
import DropFileInput from "../apps/DropFileInput.js";
import SelectedInfo from "./SelectedInfo.js";

// Wrapper: default placement to bottomRight so notifications don't appear behind headers/toolbars.
const showNotificationGlobal = (message, type = "info", placement = "bottomRight") =>
    _showNotificationGlobal(message, type, placement);

/* ─────────────────────────────────────────
   Styles
───────────────────────────────────────── */
const STORAGE_LISTVIEW_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');

  .slv-root {
    display: flex;
    flex-direction: column;
    font-family: 'DM Sans', system-ui, sans-serif;
    height: 100%;
    --slv-bg: #ffffff;
    --slv-surface: #fafaf9;
    --slv-border: rgba(0,0,0,0.07);
    --slv-border-md: rgba(0,0,0,0.12);
    --slv-text: #111110;
    --slv-muted: #6b6b68;
    --slv-subtle: #a1a19d;
    --slv-accent: #111110;
    --slv-accent-bg: rgba(17,17,16,0.06);
    --slv-blue: #2563eb;
    --slv-blue-bg: rgba(37,99,235,0.07);
    --slv-amber: #d97706;
    --slv-amber-bg: rgba(217,119,6,0.08);
    --slv-red: #dc2626;
    --slv-red-bg: rgba(220,38,38,0.08);
    --slv-green: #16a34a;
    --slv-green-bg: rgba(22,163,74,0.08);
    --slv-selected-bg: rgba(37,99,235,0.06);
    --slv-selected-border: rgba(37,99,235,0.2);
    --slv-hover: rgba(0,0,0,0.025);
    --slv-radius: 8px;
    --slv-radius-lg: 12px;
    --slv-row-h: 44px;
  }

  /* ── Toolbar ── */
  .slv-toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--slv-border);
    background: var(--slv-bg);
    flex-shrink: 0;
  }

  .slv-toolbar-left {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
  }

  .slv-toolbar-right {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
  }

  /* ── Primary toolbar buttons ── */
  .slv-tbtn {
    height: 34px;
    padding: 0 10px;
    border-radius: 10px;
    border: 1px solid var(--slv-border);
    background: var(--slv-surface);
    color: var(--slv-text);
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 12.5px;
    font-weight: 650;
    letter-spacing: -0.01em;
    transition: background 0.12s, border-color 0.12s, box-shadow 0.12s, transform 0.06s;
    flex-shrink: 0;
    user-select: none;
  }

  .slv-tbtn i { font-size: 13px; }
  .slv-tbtn:active:not(:disabled) { transform: translateY(0.5px); }
  .slv-tbtn:disabled { opacity: 0.42; cursor: not-allowed; pointer-events: none; }

  .slv-tbtn:hover:not(:disabled) {
    background: #fff;
    border-color: var(--slv-border-md);
    box-shadow: 0 8px 22px rgba(0,0,0,0.06);
  }

  .slv-tbtn.primary {
    border-color: rgba(37,99,235,0.28);
    background: rgba(37,99,235,0.08);
    color: #1d4ed8;
  }

  .slv-tbtn.primary:hover:not(:disabled) {
    background: rgba(37,99,235,0.12);
    border-color: rgba(37,99,235,0.36);
  }

  .slv-tbtn.amber {
    border-color: rgba(217,119,6,0.28);
    background: rgba(217,119,6,0.10);
    color: #b45309;
  }

  .slv-tbtn.amber:hover:not(:disabled) {
    background: rgba(217,119,6,0.14);
    border-color: rgba(217,119,6,0.36);
  }

  .slv-tbtn.ghost {
    background: #fff;
  }

  .slv-icon-btn {
    width: 34px;
    height: 34px;
    border-radius: var(--slv-radius);
    border: 1px solid var(--slv-border);
    background: var(--slv-surface);
    color: var(--slv-muted);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 13px;
    transition: border-color 0.12s, color 0.12s, background 0.12s;
    flex-shrink: 0;
  }

  .slv-icon-btn:hover {
    border-color: var(--slv-border-md);
    color: var(--slv-text);
    background: var(--slv-bg);
  }

  .slv-icon-btn:disabled {
    opacity: 0.38;
    cursor: not-allowed;
    pointer-events: none;
  }

  .slv-icon-btn.red { color: var(--slv-red); }
  .slv-icon-btn.blue { color: var(--slv-blue); }
  .slv-icon-btn.green { color: var(--slv-green); }

  .slv-searchbox {
    position: relative;
    flex: 0 1 260px;
    min-width: 160px;
    max-width: 320px;
  }

  .slv-searchbox input {
    width: 100%;
    height: 34px;
    border-radius: var(--slv-radius);
    border: 1px solid var(--slv-border);
    padding: 0 32px 0 12px;
    font-family: 'DM Sans', system-ui, sans-serif;
    font-size: 12.5px;
    color: var(--slv-text);
    background: var(--slv-surface);
    outline: none;
    transition: border-color 0.12s, box-shadow 0.12s;
  }

  .slv-searchbox input::placeholder {
    color: var(--slv-subtle);
  }

  .slv-searchbox input:focus {
    border-color: var(--slv-blue);
    box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
    background: #fff;
  }

  .slv-searchbox-ico {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--slv-subtle);
    font-size: 12px;
    cursor: pointer;
  }

  /* ── Selection action bar ── */
  .slv-actbar {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 10px;
          color: var(--slv-text);
          position: static;
          bottom: 20px;
          width: auto;
          margin-left: 16px; 
          overflow-x: auto;
          flex: 1 1 auto;
          min-width: 0%;
          max-width: 100%;     
        }

        .slv-count {
          font-weight: 600;
          font-size: 11px;
          margin-right: 4px;
          white-space: nowrap;
          color: #1d4ed8;
        }

        .slv-btn {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 6px;
          padding: 6px 8px;
          border: 1px solid rgba(0,0,0,0.08);
          border-radius: 9px;
          background: #fff;
          color: var(--slv-text);
          font-size: 11px;
          line-height: 1;
          cursor: pointer;
          white-space: nowrap;
          flex-shrink: 0;
          transition: background 0.12s ease, border-color 0.12s ease, transform 0.06s ease;
        }

        .slv-btn i {
          font-size: 13px;
        }

        .slv-btn:hover {
          background: rgba(37,99,235,0.06);
          border-color: rgba(37,99,235,0.22);
        }

        .slv-btn:active:not(:disabled) {
          transform: translateY(0.5px);
          background: rgba(37,99,235,0.09);
        }

        .slv-btn:focus-visible {
          outline: none;
          border-color: rgba(96, 165, 250, 0.75);
          box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.22);
        }

        .slv-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .slv-btn.danger {
          background: rgba(220,38,38,0.06);
          border-color: rgba(220,38,38,0.16);
          color: #dc2626;
        }

        .slv-btn.success {
          background: rgba(22,163,74,0.06);
          border-color: rgba(22,163,74,0.16);
          color: #16a34a;
        }

        .slv-btn.danger:hover { background: rgba(220, 38, 38, 0.10); border-color: rgba(220, 38, 38, 0.22); }
        .slv-btn.success:hover { background: rgba(22, 163, 74, 0.10); border-color: rgba(22, 163, 74, 0.22); }

  /* ── Table header ── */
  .slv-head {
    display: grid;
    grid-template-columns: 1.8fr 0.45fr 1fr;
    gap: 8px;
    padding: 0 14px;
    height: 34px;
    align-items: center;
    border-bottom: 1px solid var(--slv-border);
    background: var(--slv-surface);
    flex-shrink: 0;
  }

  .slv-head-cell {
    font-size: 11px;
    font-weight: 600;
    color: var(--slv-subtle);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    user-select: none;
    transition: color 0.1s;
  }

  .slv-head-cell:hover {
    color: var(--slv-muted);
  }

  .slv-head-name {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  /* ── List rows ── */
  .slv-list-body {
    padding: 6px 8px;
  }

  .slv-row {
    display: grid;
    grid-template-columns: 1.8fr 0.45fr 1fr;
    gap: 8px;
    align-items: center;
    height: var(--slv-row-h);
    padding: 0 10px;
    border-radius: var(--slv-radius);
    border: 1px solid transparent;
    transition: background 0.1s, border-color 0.1s;
    cursor: default;
    position: relative;
  }

  .slv-row:hover {
    background: var(--slv-hover);
    border-color: var(--slv-border);
  }

  .slv-row.selected {
    background: var(--slv-selected-bg);
    border-color: var(--slv-selected-border);
  }

  /* Zebra */
  .slv-list-body .slv-row:nth-child(even) { background: var(--slv-surface); }
  .slv-list-body .slv-row:nth-child(even):hover { background: var(--slv-hover); }
  .slv-list-body .slv-row:nth-child(even).selected { background: var(--slv-selected-bg); }

  .slv-row-name {
    display: flex;
    align-items: center;
    gap: 9px;
    min-width: 0;
  }

  .slv-checkbox {
    width: 16px;
    height: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--slv-blue);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.1s;
    font-size: 13px;
    flex-shrink: 0;
  }

  .slv-row:hover .slv-checkbox,
  .slv-row.selected .slv-checkbox {
    opacity: 1;
    pointer-events: auto;
  }

  .slv-file-icon {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: var(--slv-surface);
    border: 1px solid var(--slv-border);
  }

  .slv-file-icon img { width: 14px; height: 14px; }
  .slv-file-icon i { font-size: 14px; }

  .slv-row-label {
    min-width: 0;
    flex: 1;
  }

  .slv-row-label .name {
    font-size: 13px;
    font-weight: 500;
    color: var(--slv-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
    letter-spacing: -0.01em;
  }

  .slv-row-label .name.folder-name {
    cursor: pointer;
  }

  .slv-row-label .name.folder-name:hover {
    color: var(--slv-blue);
  }

  .slv-row-size {
    font-size: 12px;
    color: var(--slv-muted);
    font-weight: 400;
    white-space: nowrap;
  }

  .slv-row-date {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    font-size: 12px;
    color: var(--slv-muted);
    white-space: nowrap;
  }

  /* ── Context menu (3-dot) ── */
  .slv-ctx-btn {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: none;
    background: transparent;
    color: var(--slv-subtle);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 13px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.1s, background 0.1s, color 0.1s;
    flex-shrink: 0;
  }

  .slv-row:hover .slv-ctx-btn,
  .slv-row.selected .slv-ctx-btn {
    opacity: 1;
    pointer-events: auto;
  }

  .slv-ctx-btn:hover {
    background: rgba(0,0,0,0.06);
    color: var(--slv-text);
  }

  /* ── Action popover ── */
  .slv-popover.popover {
    border: 1px solid var(--slv-border-md);
    border-radius: var(--slv-radius-lg);
    box-shadow: 0 12px 40px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06);
    min-width: 220px;
    overflow: hidden;
    padding: 0;
  }

  .slv-popover .popover-arrow { display: none; }

  .slv-popover .popover-body {
    padding: 6px;
    background: #fff;
  }

  .slv-pop-item {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 10px;
    border-radius: var(--slv-radius);
    border: none;
    background: transparent;
    color: var(--slv-text);
    font-size: 13px;
    font-weight: 500;
    font-family: 'DM Sans', system-ui, sans-serif;
    cursor: pointer;
    text-align: left;
    letter-spacing: -0.01em;
    transition: background 0.1s;
  }

  .slv-pop-item:hover { background: var(--slv-surface); }
  .slv-pop-item:disabled { opacity: 0.4; cursor: not-allowed; }

  .slv-pop-ico {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    flex-shrink: 0;
  }

  .slv-pop-ico.blue { background: var(--slv-blue-bg); color: var(--slv-blue); }
  .slv-pop-ico.green { background: var(--slv-green-bg); color: var(--slv-green); }
  .slv-pop-ico.red { background: var(--slv-red-bg); color: var(--slv-red); }
  .slv-pop-ico.amber { background: var(--slv-amber-bg); color: var(--slv-amber); }

  .slv-pop-divider {
    height: 1px;
    background: var(--slv-border);
    margin: 4px 6px;
  }

  /* ── Upload dropdown ── */
  .slv-upload-toggle.dropdown-toggle {
    width: 34px !important;
    height: 34px !important;
    border-radius: var(--slv-radius) !important;
    border: 1px solid var(--slv-border) !important;
    background: var(--slv-surface) !important;
    color: var(--slv-text) !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 0 !important;
    font-size: 13px !important;
    transition: border-color 0.12s, color 0.12s !important;
  }

  .slv-upload-toggle.dropdown-toggle::after { display: none !important; }
  .slv-upload-toggle.dropdown-toggle:hover {
    border-color: var(--slv-border-md) !important;
    color: var(--slv-text) !important;
    background: var(--slv-bg) !important;
  }

  .slv-upload-menu.dropdown-menu {
    border: 1px solid var(--slv-border-md);
    border-radius: var(--slv-radius-lg);
    box-shadow: 0 12px 40px rgba(0,0,0,0.12);
    padding: 4px;
    min-width: 150px;
  }

  .slv-upload-menu .dropdown-item {
    border-radius: var(--slv-radius);
    font-size: 13px;
    font-weight: 500;
    color: var(--slv-text);
    padding: 6px 8px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .slv-upload-menu .dropdown-item i {
    font-size: 12px;
  }

  .slv-upload-menu .dropdown-item:hover {
    background: var(--slv-surface);
  }

  .slv-create-fold-pop.popover {
    border-radius: var(--slv-radius-lg);
    border: 1px solid var(--slv-border-md);
    box-shadow: 0 12px 40px rgba(0,0,0,0.12);
    min-width: 240px;
  }

  .slv-create-fold-pop .popover-header {
    background: var(--slv-surface);
    border-bottom: 1px solid var(--slv-border);
    font-size: 13px;
    font-weight: 600;
    color: var(--slv-text);
    border-radius: calc(var(--slv-radius-lg) - 1px) calc(var(--slv-radius-lg) - 1px) 0 0;
    padding: 10px 12px;
  }

  .slv-create-fold-pop .popover-body {
    padding: 10px 12px;
    background: #fff;
  }

  /* ── Upload (single button dropdown) ── */
  .slv-upload-dd .dropdown-toggle {
    height: 34px !important;
    padding: 0 10px !important;
    border-radius: 10px !important;
    border: 1px solid rgba(37,99,235,0.28) !important;
    background: rgba(37,99,235,0.08) !important;
    color: #1d4ed8 !important;
    display: inline-flex !important;
    align-items: center !important;
    gap: 8px !important;
    font-size: 12.5px !important;
    font-weight: 650 !important;
    letter-spacing: -0.01em !important;
    box-shadow: none !important;
    outline: none !important;
    transition: background 0.12s, border-color 0.12s, box-shadow 0.12s, transform 0.06s !important;
  }

  .slv-upload-dd .dropdown-toggle::after { margin-left: 6px; }

  /* ONLY real mouse hover gets the lifted look */
  .slv-upload-dd .dropdown-toggle:hover:not(:disabled) {
    background: rgba(37,99,235,0.12) !important;
    border-color: rgba(37,99,235,0.36) !important;
    box-shadow: 0 8px 22px rgba(0,0,0,0.06) !important;
  }

  /* Click-press feedback, only while actually held down */
  .slv-upload-dd .dropdown-toggle:active:not(:disabled) {
    transform: translateY(0.5px);
    background: rgba(37,99,235,0.16) !important;
    border-color: rgba(37,99,235,0.4) !important;
    box-shadow: 0 0 0 3px rgba(37,99,235,0.15) !important;
  }

  /* Everything else (focus, open dropdown, after click) = resting style, no hover look */
  .slv-upload-dd .dropdown-toggle:focus:not(:hover),
  .slv-upload-dd .dropdown-toggle:focus-visible:not(:hover),
  .slv-upload-dd .dropdown-toggle.show:not(:hover) {
    background: rgba(37,99,235,0.08) !important;
    border-color: rgba(37,99,235,0.28) !important;
    box-shadow: none !important;
    transform: none !important;
  }
    
  /* ── Grid view ── */
  .slv-grid {
    padding: 12px;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(168px, 1fr));
    gap: 10px;
  }

  .slv-card {
    position: relative;
    border-radius: var(--slv-radius-lg);
    border: 1px solid var(--slv-border);
    background: var(--slv-bg);
    transition: border-color 0.12s, box-shadow 0.12s, background 0.12s;
    overflow: hidden;
  }

  .slv-card:hover {
    border-color: var(--slv-border-md);
    box-shadow: 0 4px 14px rgba(0,0,0,0.07);
  }

  .slv-card.selected {
    border-color: var(--slv-selected-border);
    background: var(--slv-selected-bg);
    box-shadow: 0 4px 14px rgba(37,99,235,0.10);
  }

  .slv-card-thumb {
    height: 88px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-bottom: 1px solid var(--slv-border);
    background: var(--slv-surface);
    position: relative;
  }

  .slv-card-thumb i { font-size: 26px; }
  .slv-card-thumb img { width: 26px; height: 26px; }

  .slv-card-ctx {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 26px;
    height: 26px;
    border-radius: 6px;
    border: 1px solid var(--slv-border);
    background: rgba(255,255,255,0.85);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 12px;
    color: var(--slv-muted);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.1s, background 0.1s;
    backdrop-filter: blur(4px);
  }

  .slv-card:hover .slv-card-ctx {
    opacity: 1;
    pointer-events: auto;
  }

  .slv-card-ctx:hover {
    background: #fff;
    color: var(--slv-text);
  }

  .slv-card-check {
    position: absolute;
    top: 8px;
    left: 8px;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    border: 1px solid var(--slv-border);
    background: rgba(255,255,255,0.85);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 12px;
    color: var(--slv-blue);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.1s;
    backdrop-filter: blur(4px);
  }

  .slv-card:hover .slv-card-check,
  .slv-card.selected .slv-card-check {
    opacity: 1;
    pointer-events: auto;
  }

  .slv-card-body {
    padding: 9px 10px 10px;
  }

  .slv-card-name {
    font-size: 12.5px;
    font-weight: 500;
    color: var(--slv-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    letter-spacing: -0.01em;
  }

  .slv-card-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 3px;
    font-size: 11px;
    color: var(--slv-subtle);
  }

  /* ── Path breadcrumb ── */
  .slv-pathbar {
    flex-shrink: 0;
    padding: 8px 14px;
    border-bottom: 1px solid var(--slv-border);
    background: var(--slv-surface);
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
  }

  .slv-crumb {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    color: var(--slv-muted);
    border: 1px solid transparent;
    white-space: nowrap;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    letter-spacing: -0.01em;
  }

  .slv-crumb.clickable {
    cursor: pointer;
    transition: background 0.1s, color 0.1s, border-color 0.1s;
  }

  .slv-crumb.clickable:hover {
    background: var(--slv-accent-bg);
    color: var(--slv-text);
    border-color: var(--slv-border);
  }

  .slv-crumb.active {
    color: var(--slv-text);
    background: rgba(37,99,235,0.06);
    border-color: rgba(37,99,235,0.15);
    color: var(--slv-blue);
  }

  .slv-crumb-sep {
    color: var(--slv-subtle);
    font-size: 11px;
    user-select: none;
    padding: 0 2px;
  }

  /* ── Tag badge ── */
  .slv-folder-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 6px;
    border-radius: 4px;
    background: var(--slv-amber-bg);
    border: 1px solid rgba(217,119,6,0.15);
    font-size: 10px;
    font-weight: 600;
    color: var(--slv-amber);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    flex-shrink: 0;
  }

  /* ── Loading overlay ── */
  .slv-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px;
  }

  /* ── Page end ── */
  .slv-page-end {
    text-align: center;
    padding: 16px;
    font-size: 11.5px;
    color: var(--slv-subtle);
    letter-spacing: 0.04em;
  }

  /* ── Modals ── */
  .slv-modal .modal-content {
    border-radius: var(--slv-radius-lg);
    border: 1px solid var(--slv-border-md);
    box-shadow: 0 24px 64px rgba(0,0,0,0.12);
  }

  .slv-modal .modal-header {
    border-bottom: 1px solid var(--slv-border);
    padding: 16px 20px;
    font-size: 14px;
    font-weight: 600;
    color: var(--slv-text);
    letter-spacing: -0.01em;
  }

  .slv-modal .modal-footer {
    border-top: 1px solid var(--slv-border);
    padding: 12px 20px;
    gap: 8px;
  }

  /* ── Copy / Move modal sizing (auto expand) ── */
  .slv-op-dialog.modal-dialog {
    width: calc(100vw - 64px);
    max-width: 1280px;
    margin: 28px auto;
  }

  .slv-op-dialog .modal-content {
    height: calc(100vh - 110px);
    display: flex;
    flex-direction: column;
  }

  .slv-op-dialog .modal-body {
    padding: 0;
    flex: 1;
    overflow: hidden;
  }

  @media (max-width: 860px) {
    .slv-op-dialog.modal-dialog {
      width: calc(100vw - 24px);
      margin: 12px auto;
    }

    .slv-op-dialog .modal-content {
      height: calc(100vh - 48px);
    }
  }

  /* Scrollbar */
  .slv-scroll-area::-webkit-scrollbar { width: 4px; }
  .slv-scroll-area::-webkit-scrollbar-track { background: transparent; }
  .slv-scroll-area::-webkit-scrollbar-thumb { background: var(--slv-border-md); border-radius: 4px; }
`;

/* ─────────────────────────────────────────
   Component
───────────────────────────────────────── */
const StorageListView = (props) => {
    const folderUploadRef = useRef(null);
    let searchTimeOut = 0;

    const [searchWord, setSearchWord] = useState("");
    const [getCurrentFolder, setCurrentFolder] = useState(null);
    const [getstorageFiles, setstorageFiles] = useState([]);
    const [getstorageFolders, setstorageFolders] = useState([]);
    const [storageFilesToBeAdded, setStorageFilesToBeAdded] = useState(props.selectedData?.files || []);
    const [storageFoldersToBeAdded, setStorageFoldersToBeAdded] = useState(props.selectedData?.folders || []);
    const [getnextPageToken, setnextPageToken] = useState(null);
    const [storageNavigations, setStorageNavigations] = useState(null);
    const [showUploadModel, setShowUploadModel] = useState(false);
    const [showFolderUploadModel, setShowFolderUploadModel] = useState(false);
    const [loadMoreFlag, setLoadMoreFlag] = useState(false);
    const [folderUploadFiles, setFolderUploadFiles] = useState([]);
    const [folderToBeCreate, setFolderToBeCreate] = useState("");
    const [renameToBeCreate, setRenameToBeCreate] = useState("");
    const [getSortType, setSortType] = useState("desc");
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [showCopyMoveModal, setShowCopyMoveModal] = useState(false);
    const [openSelectedFilesDrawer, setOpenSelectedFilesDrawer] = useState(false);
    const [modeTransfer, setModeTransfer] = useState("");
    const [showLoading, setShowLoading] = useState(false);
    const [selectAllFlag, setSelectAllFlag] = useState(false);
    const [getMoveCopyConfig, setMoveCopyConfig] = useState({ source: "", type: "", isDir: false });
    const presentFolderRef = useRef(null);
    const [totalFiles, setTotalFiles] = useState(0);
    const [currentUploadingFile, setCurrentUploadingFile] = useState(null);
    const [trashMod, setTrashMod] = useState(false);
    const [deleteType, setDeleteType] = useState("");
    const [restoreMod, setRestoreMod] = useState(false);
    const [createFoldPop, setCreateFoldPop] = useState(false);
    const [confirmFldMod, setConfirmFldMod] = useState(false);
    const [actionMenu, setActionMenu] = useState({ show: false, target: null, item: null });

    const openActionMenu = (e, item) => {
        e.preventDefault();
        e.stopPropagation();
        setActionMenu({ show: true, target: e.currentTarget, item });
    };
    const closeActionMenu = () => setActionMenu({ show: false, target: null, item: null });

    const buildActionEntries = (item) => {
        const entries = [];
        const isFile = item?.name !== undefined;
        const isZip = isFile ? checkZipExtension(item?.name) : false;

        if (isFile && !isZip)
            entries.push({ label: "View", icoClass: "blue", icon: "fas fa-binoculars text-primary", onClick: () => onViewClick(item) });

        if (!props?.trashFileFlag) {
            entries.push({ label: "Get sharable link", icoClass: "blue", icon: "fas fa-link text-primary", onClick: () => onShareClick(item) });
            entries.push({ divider: true });
            entries.push({ label: "Copy", icoClass: "blue", icon: "fas fa-clone text-primary", onClick: () => { onSingleFileMoveCopy(item); onOpenCopyMoveModal("Copy"); setModeTransfer("Copy"); } });
            entries.push({ label: "Move", icoClass: "amber", icon: "fas fa-file-export text-warning", onClick: () => { onSingleFileMoveCopy(item); onOpenCopyMoveModal("Move"); setModeTransfer("Move"); } });
            if (isFile)
                entries.push({ label: "Download", icoClass: "blue", icon: "fas fa-download text-primary", onClick: () => downloadFiles(item.name) });
            entries.push({ divider: true });
            entries.push({ label: "Rename", icoClass: "amber", icon: "fa-regular fa-pen-to-square text-warning", onClick: () => onRenameClick(item) });
            entries.push({ label: "Duplicate", icoClass: "blue", icon: "fas fa-copy text-primary", onClick: () => onDuplicateClick(item) });
            if (!isFile) {
                entries.push({label: "Compress",icoClass: "blue",icon: "fas fa-file-zipper text-primary",onClick: () => onCompressClick(item)});}
            entries.push({ label: "Copy path", icoClass: "blue", icon: "fas fa-clipboard text-primary", onClick: () => onCopyPathClick(item) });
            if (!isFile && isZip)
                entries.push({ label: "Extract", icoClass: "amber", icon: "fas fa-box-open text-warning", onClick: () => { onSingleFileMoveCopy(item); setTimeout(() => document.getElementById("slv-extract")?.click(), CALLBACKTIMEOUT); } });
            entries.push({ label: "Move to trash", icoClass: "red", icon: "fas fa-trash-alt text-danger", onClick: () => { onSingleFileMoveCopy(item); onActionTrashClick("Trash"); } });
        } else {
            entries.push({ label: "Restore", icoClass: "green", icon: "fas fa-trash-restore text-success", onClick: () => { onSingleFileMoveCopy(item); setTimeout(() => document.getElementById("slv-restore")?.click(), CALLBACKTIMEOUT); } });
            entries.push({ label: "Delete permanently", icoClass: "red", icon: "fas fa-trash-alt text-danger", onClick: () => { onSingleFileMoveCopy(item); onActionTrashClick("Delete"); } });
        }
        return entries;
    };

    useEffect(() => {
        const id = "slv-styles-v2";
        if (!document.getElementById(id)) {
            const tag = document.createElement("style");
            tag.id = id;
            tag.textContent = STORAGE_LISTVIEW_CSS;
            document.head.appendChild(tag);
        }
    }, []);

    useEffect(() => {
        resetValues();
        let prefixCurrent = props.prefixCurrent !== null ? props.prefixCurrent : presentFolderRef.current;
        setCurrentFolder(prefixCurrent);
        fetchStorageFilesFolders(prefixCurrent, null, [], []);
        if (props.storageId) setSelectAllFlag(false);

        const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
        const handleDrop = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e?.dataTransfer?.files.length > 0) {
                const files = e.dataTransfer.files;
                setTotalFiles(files.length);
                for (let i = 0; i < files.length; i++) convertToFile(files[i]);
            }
        };
        window.addEventListener("dragover", handleDragOver);
        window.addEventListener("drop", handleDrop);
        return () => { window.removeEventListener("dragover", handleDragOver); window.removeEventListener("drop", handleDrop); };
    }, [props.storageId, props.rootFolder, props.trashFileFlag, props.prefixCurrent]);

    useEffect(() => {
        window.addEventListener("storageMessageReceived", handleMessageReceived);
        return () => window.removeEventListener("storageMessageReceived", handleMessageReceived);
    }, []);

    const handleMessageReceived = (event) => {
        const message = event?.detail;
        if (message?.ninfo?.action === websocketAction.RESULT)
            fetchStorageFilesFolders(presentFolderRef.current, null, [], []);
    };

    const resetValues = () => {
        setstorageFiles([]); setstorageFolders([]); setStorageNavigations(null);
        setStorageFilesToBeAdded([]); setStorageFoldersToBeAdded([]);
        setnextPageToken(null); setSearchWord("");
    };

    const fetchStorageFilesFolders = (prefix, pageToken, files, folders) => {
        setShowLoading(true);
        if (props.trashFileFlag) { _fetchStorageItems_trash(prefix, null, [], []); return; }
        StorageService.listObjects(prefix, props.storageId, props.rootFolder, pageToken)
            .then((response) => {
                const foldersSort = [...response?.prefixes].sort((a, b) =>
                    (typeof a === "string" ? a : a.toString()).toLowerCase()
                        .localeCompare((typeof b === "string" ? b : b.toString()).toLowerCase()) * -1
                );
                const filesSort = [...response.result].sort((a, b) => {
                    const _a = (typeof a.name === "string" ? a.name : a.name.toString()).toLowerCase();
                    const _b = (typeof b.name === "string" ? b.name : b.name.toString()).toLowerCase();
                    return _b.localeCompare(_a);
                });
                files = [...files, ...filesSort];
                folders = [...folders, ...foldersSort];
                setnextPageToken(response.next_page_token);
                setstorageFiles(files); setstorageFolders(folders);
                setSelectAllFlag(false); setCurrentFolder(presentFolderRef.current);
                setLoadMoreFlag(false); setShowLoading(false);
                setShowUploadModel(false); setShowFolderUploadModel(false);
                constructNavigation(prefix); setSearchWord(""); setCurrentUploadingFile(null);
            }).catch(() => setShowLoading(false));
    };

    const clickSorting = (type, sortType) => {
        const foldersSort = [...getstorageFolders].sort((a, b) => {
            a = (typeof a === "string" ? a : a.toString()).toLowerCase();
            b = (typeof b === "string" ? b : b.toString()).toLowerCase();
            return type === "asc" ? a.localeCompare(b) : b.localeCompare(a);
        });
        const filesSort = [...getstorageFiles].sort((a, b) => {
            const _a = (typeof a[sortType] === "string" ? a[sortType] : a[sortType].toString()).toLowerCase();
            const _b = (typeof b[sortType] === "string" ? b[sortType] : b[sortType].toString()).toLowerCase();
            if (sortType === "name") return type === "asc" ? _a.localeCompare(_b) : _b.localeCompare(_a);
            if (sortType === "size") return type === "asc" ? _a - _b : _b - _a;
            if (sortType === "created") return type === "asc" ? (_a < _b ? -1 : _a > _b ? 1 : 0) : (_b < _a ? -1 : _b > _a ? 1 : 0);
        });
        setstorageFiles(filesSort); setstorageFolders(foldersSort);
    };

    const _fetchStorageItems_trash = (prefix, pageToken, files, folders) => {
        let trashPath = (prefix || "").replace("trash/", "");
        setShowLoading(true);
        StorageService.trashObject(trashPath, props.storageId, props.rootFolder, pageToken)
            .then((response) => {
                files = [...(files || []), ...response.result];
                folders = [...(folders || []), ...response.prefixes];
                setnextPageToken(response.next_page_token);
                setstorageFiles(files); setstorageFolders(folders);
                constructNavigation(prefix); setShowLoading(false); setSearchWord("");
            }).catch(() => setShowLoading(false));
    };

    const constructNavigation = (currentFolder) => {
        const sep = (key) => (
            <span key={key} className="slv-crumb-sep" aria-hidden="true">/</span>
        );

        let homeNode = currentFolder ? (
            <span className="slv-crumb clickable" data-name="" onClick={onNavigationClick}
                role="button" tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onNavigationClick({ target: { dataset: { name: "" } } }); } }}
                title="Home">
                <i className="fas fa-home" aria-hidden="true" />
                <span>Home</span>
            </span>
        ) : (
            <span className="slv-crumb active" title="Home">
                <i className="fas fa-home" aria-hidden="true" />
                <span>Home</span>
            </span>
        );

        let navigations = [homeNode];
        if (currentFolder) {
            let prefixes = currentFolder.split("/").slice(0, -1);
            let lastPrefix = prefixes.length > 0 ? prefixes.pop() : null;
            let name = "";
            for (const prefix of prefixes) {
                name += prefix + "/";
                navigations.push(sep(`sep-${name}`));
                navigations.push(
                    <span key={name} className="slv-crumb clickable" data-name={name} onClick={onNavigationClick}
                        role="button" tabIndex={0}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onNavigationClick({ target: { dataset: { name } } }); } }}
                        title={prefix}>
                        <span>{prefix}</span>
                    </span>
                );
            }
            if (lastPrefix) {
                navigations.push(sep(`sep-${lastPrefix}`));
                navigations.push(<span key={lastPrefix} className="slv-crumb active" title={lastPrefix}><span>{lastPrefix}</span></span>);
            }
        }
        setStorageNavigations(navigations);
    };

    const onNavigationClick = (event) => {
        if (event) {
            // currentTarget = the crumb the handler is attached to (not the inner <span>/<i>)
            const item = event.currentTarget?.dataset?.name ?? event.target?.dataset?.name;
            if (selectAllFlag) { confirmationFolderClick(item); return; }
            folderClickPath(item);
        }
    };

    const generateUid = () => `rc-upload-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

    const onFolderClick = (item) => {
        presentFolderRef.current = item;
        setCurrentFolder(item);
        if (item) {
            if (selectAllFlag) { confirmationFolderClick(item); return; }
            folderClickPath(item);
        }
    };

    const confirmationFolderClick = (item) => setConfirmFldMod(true);

    const folderClickPath = (item) => {
        setCurrentFolder(item);
        presentFolderRef.current = item;
        setstorageFiles([]); setstorageFolders([]); setnextPageToken(null);
        fetchStorageFilesFolders(item, null, [], []);
    };

    const trimFolderName = (folderName) => {
        if (folderName.endsWith("/")) folderName = folderName.slice(0, -1);
        return folderName.split("/").pop();
    };
    const trimFileName = (fileName) => fileName.split("/").pop();

    const updateStorageFiles = (storageFile, addFlag) => {
        if (addFlag) {
            storageFile.isDir = false;
            storageFile.sourcePath = storageFile.filePath;
            const totalInStorage = getstorageFiles.length + getstorageFolders.length;
            setStorageFilesToBeAdded((prev) => {
                const updated = [...prev, storageFile];
                const unique = updated.filter((item, idx) => idx === updated.findIndex((e) => e.filePath === item.filePath));
                if (totalInStorage === unique.length + storageFoldersToBeAdded.length) setSelectAllFlag(true);
                return unique;
            });
        } else {
            setStorageFilesToBeAdded(storageFilesToBeAdded.filter(
                (item) => !(item.filePath === storageFile.filePath && item.storageId === storageFile.storageId)
            ));
        }
    };

    const updateStorageFolders = (storageFolder, addFlag) => {
        if (addFlag) {
            storageFolder.isDir = true;
            storageFolder.sourcePath = storageFolder.filePath;
            const totalInStorage = getstorageFiles.length + getstorageFolders.length;
            setStorageFoldersToBeAdded((prev) => {
                const updated = [...prev, storageFolder];
                const unique = updated.filter((item, idx) => idx === updated.findIndex((e) => e.filePath === item.filePath));
                if (totalInStorage === storageFilesToBeAdded.length + unique.length) setSelectAllFlag(true);
                return unique;
            });
        } else {
            setStorageFoldersToBeAdded(storageFoldersToBeAdded.filter(
                (item) => !(item.filePath === storageFolder.filePath && item.storageId === storageFolder.storageId)
            ));
        }
    };

    const fileCheckClicked = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const isChecked = event.target.dataset.checked === "1";
        event.target.dataset.checked = isChecked ? "0" : "1";
        if (!isChecked) {
            event.target.className = "fas fa-check-square";
        } else {
            event.target.className = "far fa-square";
            setSelectAllFlag(false);
        }
        const selectedFile = {
            storageId: props.storageId,
            rootFolder: props.rootFolder,
            filePath: event.target.dataset.name,
            envName: "",
            storageName: props.storageName,
            cloudType: props.cloudType,
        };
        event.target.dataset.type === "folder"
            ? updateStorageFolders(selectedFile, !isChecked)
            : updateStorageFiles(selectedFile, !isChecked);
    };

    const onRefresh = () => {
        setstorageFiles([]); setstorageFolders([]);
        setStorageFilesToBeAdded([]); setStorageFoldersToBeAdded([]);
        setSelectAllFlag(false); setCurrentFolder("");
        fetchStorageFilesFolders(presentFolderRef.current || "", null, [], []);
    };

    const _clearAllSeleted = () => {
        setOpenSelectedFilesDrawer(false);
        setStorageFilesToBeAdded([]); setStorageFoldersToBeAdded([]); setSelectAllFlag(false);
    };
    
    const onCreateFolderClick = () => {

        const folderName = folderToBeCreate?.trim();

        if (!folderName) {
            showNotificationGlobal(
                "Folder name is required",
                "error",
                "bottomRight"
            );
            return;
        }
        if (!folderNameWithSpaceRegex.test(folderName)) {
            showNotificationGlobal(
                "Folder name can only contain letters, numbers, spaces and underscore (_)",
                "error",
                "bottomRight"
            );
            return;
        }

        let prefixName = path.join(
            getCurrentFolder || "",
            folderName
        );
        if (!prefixName.endsWith("/")) prefixName += "/";
        presentFolderRef.current = prefixName;
        setFolderToBeCreate("");
        handleCreatePrefix(prefixName);
    };

    const handleCreatePrefix = (prefixName) => {
        StorageService.createPrefix(prefixName, props.storageId, props.rootFolder)
            .then(() => { setFolderToBeCreate(""); setCurrentFolder(""); fetchStorageFilesFolders(prefixName, null, [], []); })
            .catch(() => {});
    };

    const handleScroll = (e) => {
        e.preventDefault();
        const bottom = Math.round(e.target.scrollHeight) - Math.round(e.target.scrollTop) === Math.round(e.target.clientHeight);
        if (bottom && getnextPageToken) {
            setLoadMoreFlag(true);
            fetchStorageFilesFolders(presentFolderRef.current, getnextPageToken, getstorageFiles, getstorageFolders);
        }
    };

    const showFolderUpload = () => folderUploadRef.current.click();

    const toggleFolderUpload = () => setShowFolderUploadModel((v) => !v);

    const handleFolderUploadFiles = (e) => {
        setFolderUploadFiles([...e.target.files]);
        toggleFolderUpload();
    };

    async function getStorageDetail() {
        const configArray = await getAllStorageConfig();
        if (configArray?.length > 0) {
            const storageDetails = configArray.filter((item) => item.id === props.storageId);
            if (storageDetails.length > 0) return storageDetails[0];
        }
    }

    const onViewClick = (item) => { if (item) openStorageView(item.name, item.size); };
    const onShareClick = (item) => { if (item) GetLink(item); };

    const onRenameClick = (item) => {
        if (item) {
            const name = item.name || item;
            const isDirectory = !item.name;
            copyMoveConfig(name, "Move", isDirectory);
            setRenameToBeCreate(popPathName(name));
            toggleRenameModal();
        }
    };

    const onExtractClick = () => {
        if (storageFoldersToBeAdded.length > 0) { showNotificationGlobal("Please select compressed files only", "error"); setStorageFoldersToBeAdded([]); return; }
        if (!checkValidFile()) { showNotificationGlobal("Please select compressed files only", "error"); setOpenSelectedFilesDrawer(true); return; }
        GetExtract();
    };

    const onCompressClick = (item) => {
        if (!item) {
            showNotificationGlobal("Missing folder info", "error");
            return;
        }

        const compressData = {
            sourceStorage: {
                storageId: props.storageId,
                rootFolder: props.rootFolder || "",
                apiKey: "",
            },
            paths: [
                {
                    sourcePath: item,
                    isDir: true,
                },
            ],
            destinationStorage: {
                storageId: props.storageId,
                rootFolder: props.rootFolder || "",
                apiKey: "",
            },
            destinationPath: "",
        };

        FileJobsService.CompressFile(compressData)
            .then((response) => {
                if (response?.status === API_RESPONSE.SUCCESS) {
                    showNotificationGlobal(
                        "Compression initiated",
                        "success"
                    );

                    setTimeout(() => {
                        onRefresh();
                    }, 2000);
                }
            })
            .catch((err) => {
                showNotificationGlobal(
                    err?.message || "Compression failed",
                    "error"
                );
            });
    };

    const checkValidFile = () => { for (let f of storageFilesToBeAdded) return checkZipExtension(f.filePath); };

    const copyMoveConfig = (name, type, flag) => setMoveCopyConfig({ source: name, type, isDir: flag });

    const toggleRenameModal = () => setShowRenameModal((v) => !v);

    const handleRenameChange = (event) => {
        if (event) setRenameToBeCreate(event.target.value.replace(/[^\w.\- ]/g, "").replace(/^ +/, "").replace(/ {2,}/g, " "));
    };

        const renameUpdate = (event) => {
        event.preventDefault();

        // Remove spaces at the start/end, and before the extension ("my file .pdf" -> "my file.pdf")
        const newName = renameToBeCreate
            .trim()
            .replace(/\s+(\.[^.\s]+)$/, "$1");

        if (!newName || !/[a-zA-Z0-9]/.test(newName)) {
            showNotificationGlobal("Please enter a valid name", "error");
            return;
        }

        // Same name as before: do nothing. Sending it makes the backend overwrite and delete the item.
        const currentName = popPathName(getMoveCopyConfig.source);
        if (newName === currentName) {
            setShowRenameModal(false);
            showNotificationGlobal("The new name is identical to the current name. Please provide a different name.", "info");
            return;
        }

        toggleRenameModal();
        renameFile(newName);
    };

    const renameFile = async (rename) => {
        try {
            const renameData = {
                isDir: getMoveCopyConfig.isDir,
                newName: rename,
                sourcePath: getMoveCopyConfig.source,
                sourceStorage: { rootFolder: props.rootFolder, storageId: props.storageId },
            };
            if (props.storageId === null) delete renameData.sourceStorage;
            FileJobsService.RenameFile(renameData).then((response) => {
                if (response?.status === API_RESPONSE.SUCCESS) {
                    showNotificationGlobal("Renamed successfully", "success", "bottomRight");
                    setTimeout(() => { 
                        onRefresh();
                     }, 2000);
                }
            }).catch(() => onRefresh());
        } catch { onRefresh(); }
    };

    const onDuplicateClick = (item) => {
        if (!item) { showNotificationGlobal("Missing file info", "error"); return; }
        const name = item.name || item;
        const isDirectory = !item.name;
        const localFiles = isDirectory ? getstorageFolders : getstorageFiles;
        let lastName, destPath;
        if (isDirectory) {
            const split = name.split("/");
            lastName = split[split.length - 2];
            destPath = name.split("/").slice(0, -2).join("/");
        } else {
            destPath = name.substring(0, name.lastIndexOf("/"));
            lastName = name.substring(name.lastIndexOf("/") + 1);
        }
        const newName = duplicateFilePath(localFiles, lastName, isDirectory);
        const duplicateData = {
            destinationPath: destPath !== "" ? destPath + "/" : "",
            paths: [{ isDir: isDirectory, sourcePath: name, newName }],
            sourceStorage: { rootFolder: props.rootFolder, storageId: props.storageId },
        };
        if (props.storageId === null) delete duplicateData.sourceStorage;
        FileJobsService.CopyFile(duplicateData).then((response) => {
            if (response?.status === API_RESPONSE.SUCCESS) { onRefresh(); showNotificationGlobal("Duplicate created", "success"); }
        });
    };

    const GetExtract = () => {
        if (!storageFilesToBeAdded?.length) { showNotificationGlobal("Missing file info", "error"); return; }
        const selectedData = storageFilesToBeAdded.map((item) => ({ isDir: item.isDir, sourcePath: item.sourcePath }));
        const extractdata = {
            destinationPath: getCurrentFolder || "/",
            destinationStorage: { rootFolder: props.rootFolder, storageId: props.storageId },
            paths: selectedData,
            sourceStorage: { rootFolder: props.rootFolder, storageId: props.storageId },
        };
        FileJobsService.ExtractFile(extractdata).then((response) => {
            if (response?.status === API_RESPONSE.SUCCESS) { onRefresh(); showNotificationGlobal("Extraction initiated", "success"); }
        });
    };

    async function getAllStorageConfig() {
        return StorageConfigService.getAllStorageConfig().then((response) => {
            if (response.status === "SUCCESS") return response.storageDTOPage.content;
            showNotificationGlobal(response?.errorMessage || "Failed to load storage config", "error");
        });
    }

    const onCopyPathClick = (item) => {
        if (!item) { showNotificationGlobal("Missing file info", "error"); return; }
        getAllStorageConfig().then((configArray) => {
            if (!configArray?.length) return;
            const filterArray = configArray.filter((c) => c.id === props.storageId);
            if (!filterArray.length) return;
            const { name: sName, platformDefault } = filterArray[0];
            const prefixPath = platformDefault ? STORAGE_DEFAULT_MOUNT_PATH : `${STORAGE_EXTERNAL_MOUNT_PATH}${sName}/${props.rootFolder}/`;
            const name = item.name || item;
            const textarea = document.createElement("textarea");
            textarea.innerText = prefixPath + name;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand("copy");
            textarea.remove();
            showNotificationGlobal("Path copied", "success");
        });
    };

    const GetLink = (item) => {
        const name = item.name || item;
        const isDirectory = !item.name;
        localStorage.setItem("filePathStorage", name);
        const currentUrl = window.location.href;
        const reg = "/storage" + window.location.search;
        const redirectUrl = !isDirectory
            ? currentUrl.replace(reg, `/storageView?storage=${props.storageId}&root=${props.rootFolder}&file=${name}`)
            : currentUrl.replace(reg, `/storage?storage=${props.storageId}&root=${props.rootFolder}&path=${name}`);
        generateTinyUrl(redirectUrl);
    };

    const openStorageView = (name, fileSize) => {
        getStorageDetail().then((details) => {
            const storageInfo = details ? `&storageName=${details.name}&platformDefault=${details.platformDefault}&filesize=${fileSize}` : "";
            const currentUrl = window.location.href.split("?path=")[0];
            const redirectUrl = currentUrl.replace(
                "/storage",
                `/storageView?storage=${props.storageId}&root=${props.rootFolder}&file=${name}&filesize=${fileSize}${storageInfo}`
            );
            window.open(redirectUrl, "_blank");
        });
    };

    const popPathName = (p) => {
        if (!p) return "";
        return p.replace(/\/$/, "").split("/").pop();
    };

    const onSingleFileMoveCopy = (item) => {
        if (!item) return;
        const type = item.hasOwnProperty("name") ? "file" : "folder";
        const selectedFile = {
            storageId: props.storageId,
            rootFolder: props.rootFolder,
            filePath: type === "file" ? item.name : item,
            envName: "",
            storageName: props.storageName,
            cloudType: props.cloudType,
        };
        type === "folder" ? updateStorageFolders(selectedFile, true) : updateStorageFiles(selectedFile, true);
    };

    const onOpenCopyMoveModal = (type) => {
        if (storageFilesToBeAdded.length + storageFoldersToBeAdded.length > MAX_COPY_MOVE_FILES) {
            props._setCommonMessage(`You can ${type.toLowerCase()} up to ${MAX_COPY_MOVE_FILES} files`);
            return;
        }
        props._setCommonMessage("");
        setShowCopyMoveModal(true);
    };

    const _getSelectedFiles = (data) => { if (!data) setShowCopyMoveModal(false); };

    const _getStorageDetail = (destinationData) => {
        setShowCopyMoveModal(false);
        const combined = [...storageFilesToBeAdded, ...storageFoldersToBeAdded];
        const selectedData = combined.map((item) => ({ isDir: item.isDir, sourcePath: item.sourcePath }));
        if (modeTransfer === "Copy") onActionCopyClick(destinationData, selectedData);
        if (modeTransfer === "Move") onActionMoveClick(destinationData, selectedData);
    };

    const onActionMoveClick = async (destinationData, selectedData) => {
        const moveData = await constructJobOperationData(destinationData, selectedData);
        if (moveData) {
            FileJobsService.MoveFile(moveData)
                .then((response) => {
                    // Some FileJobs endpoints don't return a status payload; treat "no response" as success.
                    const ok =
                        response == null ||
                        response?.status === API_RESPONSE.SUCCESS ||
                        response?.status === "SUCCESS" ||
                        response?.status === true;
                    if (!ok) {
                        showNotificationGlobal(response?.errorMessage || response?.message || "Move failed", "error");
                        return;
                    }
                    const count = storageFilesToBeAdded.length + storageFoldersToBeAdded.length;
                    props._setCommonMessage(`${count} file(s) moved`);
                    showNotificationGlobal(`${count} item(s) moved successfully`, "success");
                    _clearAllSeleted();
                    presentFolderRef.current = moveData.destinationPath;
                    setTimeout(() => fetchStorageFilesFolders(moveData.destinationPath, null, [], []), 1000);
                })
                .catch((err) => showNotificationGlobal(err?.message || "Move failed", "error"));
        }
    };

    const onActionCopyClick = async (destinationData, selectedData) => {
        const copyData = await constructJobOperationData(destinationData, selectedData);
        if (copyData) {
            FileJobsService.CopyFile(copyData)
                .then((response) => {
                    // Some FileJobs endpoints don't return a status payload; treat "no response" as success.
                    const ok =
                        response == null ||
                        response?.status === API_RESPONSE.SUCCESS ||
                        response?.status === "SUCCESS" ||
                        response?.status === true;
                    if (!ok) {
                        showNotificationGlobal(response?.errorMessage || response?.message || "Copy failed", "error");
                        return;
                    }
                    const count = storageFilesToBeAdded.length + storageFoldersToBeAdded.length;
                    props._setCommonMessage(`${count} file(s) copied`);
                    showNotificationGlobal(`${count} item(s) copied successfully`, "success");
                    _clearAllSeleted();
                    presentFolderRef.current = copyData.destinationPath;
                    setTimeout(() => fetchStorageFilesFolders(copyData.destinationPath, null, [], []), 1000);
                })
                .catch((err) => showNotificationGlobal(err?.message || "Copy failed", "error"));
        }
    };

    const onActionDownloadClick = async () => {
        if (storageFoldersToBeAdded.length > 0) { showNotificationGlobal("Download is available for files only", "error"); return; }
        if (storageFilesToBeAdded.length > MAX_DOWNLOAD_FILES) { showNotificationGlobal("Please select only one file to download", "error"); return; }
        for (let [index, data] of storageFilesToBeAdded.entries()) {
            await downloadFiles(data.filePath);
            if (index === storageFilesToBeAdded.length - 1) _clearAllSeleted();
        }
    };

    const downloadFiles = async (name) => {
        if (!name) return;
        const sName = props.storageId || "";
        const root = props.rootFolder || "";
        try {
            const response = await StorageService.downloadObjectUrl(name, sName, root);
            if (!response?.download_url) {
                showNotificationGlobal("Download link not available", "error");
                return;
            }
            const link = document.createElement("a");
            link.href = response.download_url;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            showNotificationGlobal(err?.message || "Download failed", "error");
        }
    };
    
    const onActionTrashClick = (type) => { setTrashMod(true); setDeleteType(type || ""); };
    const onActionRestoreClick = () => setRestoreMod(true);

    async function constructJobSameLocation(sourceMove) {
        const newPathData = [];
        for (let [index, item] of sourceMove.entries()) {
            const rename = item.isDir
                ? item.sourcePath.split("/").slice(-2)[0]
                : item.sourcePath.split("/").pop().split(".")[0];
            item.newName = `${rename}_${Date.now()}`;
            newPathData.push(item);
        }
        return newPathData;
    }

    const constructJobOperationData = async (destination, sourceMove) => {
        let pathsData = sourceMove;
        if (destination.destinationPath === getCurrentFolder) pathsData = await constructJobSameLocation(sourceMove);
        const data = {
            destinationPath: destination.destinationPath,
            destinationStorage: {
                rootFolder: destination.destinationRootFolder || null,
                storageId: destination.destinationStorageId || null,
            },
            paths: pathsData,
            sourceStorage: { rootFolder: props.rootFolder, storageId: props.storageId },
        };
        if (props.storageId === null) delete data.sourceStorage;
        if (destination.destinationStorageId === null) delete data.destinationStorage;
        return data;
    };

    const resetSelectedFilesFolders = () => { setStorageFilesToBeAdded([]); setStorageFoldersToBeAdded([]); };

    const onSelectAll = (event) => {
        if (event.target.checked) {
            setStorageFilesToBeAdded([]); setStorageFoldersToBeAdded([]); setSelectAllFlag(true);
            for (const folder of getstorageFolders) {
                updateStorageFolders({ storageId: props.storageId, rootFolder: props.rootFolder, filePath: folder, envName: "", storageName: props.storageName, cloudType: props.cloudType }, true);
            }
            for (const file of getstorageFiles) {
                updateStorageFiles({ storageId: props.storageId, rootFolder: props.rootFolder, filePath: file.name, envName: "", storageName: props.storageName, cloudType: props.cloudType }, true);
            }
        } else {
            _clearAllSeleted();
        }
    };

    const convertToFile = (fileObj) => {
        if (fileObj instanceof File) { if (!fileObj.uid) fileObj.uid = generateUid(); return fileObj; }
        const { name, type, lastModified } = fileObj;
        const fileData = new Blob([], { type: type || "application/octet-stream" });
        const newFile = new File([fileData], name, { type: type || "application/octet-stream", lastModified: lastModified || Date.now() });
        newFile.uid = generateUid();
        return newFile;
    };

    const onSearch = (value) => {
        if (typeof value === "object") { searchWithTimeout(value.target.value); return; }
        if (value !== "") searchWithTimeout(value);
    };

    const searchWithTimeout = (value) => {
        setSearchWord(value);
        if (searchTimeOut) clearTimeout(searchTimeOut);
        searchTimeOut = setTimeout(() => searchWithPrefixes(value), 1000);
    };

    const searchWithPrefixes = (value) => {
        if (!value) { onRefresh(); return; }
        StorageService.listObjectFilter(getCurrentFolder || "", value, props.storageId, props.rootFolder)
            .then((response) => {
                setstorageFiles(response.result);
                setstorageFolders(response.prefixes);
                setnextPageToken(response.next_page_token);
            }).catch(console.error);
    };

    const handleTrashDelete = () => {
        const combined = [...storageFilesToBeAdded, ...storageFoldersToBeAdded];
        const selectedData = combined.map((item) => ({ isDir: item.isDir, sourcePath: item.sourcePath }));
        const basePayload = {
            destinationPath: "",
            paths: selectedData,
            sourceStorage: { rootFolder: props.rootFolder, storageId: props.storageId },
        };
        if (props.storageId === null) delete basePayload.sourceStorage;

        const service = deleteType === "Trash" ? FileJobsService.TrashFolder : FileJobsService.DeleteFolder;
        service(basePayload).then((response) => {
            showNotificationGlobal(response?.message || "Success", "success");
            _fetchStorageItems_trash(presentFolderRef.current, null, [], []);
            resetSelectedFilesFolders();
            setTrashMod(false);
            setTimeout(() => fetchStorageFilesFolders(presentFolderRef.current, null, [], []), 2000);
        });
    };

    const handleRestore = () => {
        const combined = [...storageFilesToBeAdded, ...storageFoldersToBeAdded];
        const selectedData = combined.map((item) => ({ isDir: item.isDir, sourcePath: item.sourcePath }));
        const restoredata = {
            destinationPath: "",
            paths: selectedData,
            sourceStorage: { rootFolder: props.rootFolder, storageId: props.storageId },
        };
        if (props.storageId === null) delete restoredata.sourceStorage;
        FileJobsService.RestoreFile(restoredata).then((response) => {
            showNotificationGlobal(response?.message || "Success", "success");
            setTimeout(() => _fetchStorageItems_trash(presentFolderRef.current, null, [], []), 2000);
            setRestoreMod(false);
            resetSelectedFilesFolders();
        });
    };

    const generatePrefix = () => getCurrentFolder || "";

    const selectionCount = storageFilesToBeAdded.length + storageFoldersToBeAdded.length;

    const _removeSingleItem = (item, index) => {
        if (item.isDir) {
            setStorageFoldersToBeAdded(storageFoldersToBeAdded.filter((_, i) => i !== index));
        } else {
            setStorageFilesToBeAdded(storageFilesToBeAdded.filter((_, i) => i !== index));
        }
      };        

    /* ─── Render ─── */
    return (
        <>
            <div className="slv-root">
                {/* ── Toolbar ── */}
                <div className="slv-toolbar">
                    <div className="slv-toolbar-left">
                        <OverlayTrigger placement="bottom" overlay={<Tooltip id="tt-refresh">Refresh</Tooltip>}>
                            <button
                                type="button"
                                className="slv-icon-btn"
                                onClick={onRefresh}
                                aria-label="Refresh"
                            >
                                <i className="fa-solid fa-repeat" />
                            </button>
                        </OverlayTrigger>

                        <div className="slv-searchbox">
                            <input
                                type="search"
                                value={searchWord}
                                placeholder="Search files and folders…"
                                onChange={onSearch}
                                aria-label="Search files and folders"
                            />
                            <span className="slv-searchbox-ico" aria-hidden="true">
                                <i className="fa-solid fa-magnifying-glass" />
                            </span>
                        </div>

                        {/* Selection action bar */}
                        {selectionCount > 0 && (
                            <div className="slv-actbar">
                                <span className="slv-count">{selectionCount} selected</span>

                                <button
                                    className="slv-btn"
                                    disabled={props.trashFileFlag}
                                    onClick={() => {
                                        onOpenCopyMoveModal("Move");
                                        setModeTransfer("Move");
                                    }}
                                >
                                    <i className="fas fa-file-export" />
                                    <span>Move</span>
                                </button>

                                <button
                                    className="slv-btn"
                                    disabled={props.trashFileFlag}
                                    onClick={() => {
                                        onOpenCopyMoveModal("Copy");
                                        setModeTransfer("Copy");
                                    }}
                                >
                                    <i className="fas fa-copy" />
                                    <span>Copy</span>
                                </button>

                                <button
                                    className="slv-btn"
                                    disabled={props.trashFileFlag || storageFoldersToBeAdded.length > 0}
                                    onClick={onActionDownloadClick}
                                >
                                    <i className="fas fa-download" />
                                    <span>Download</span>
                                </button>


                                {!props.trashFileFlag ? (
                                    <button
                                        className="slv-btn danger"
                                        onClick={() => onActionTrashClick("Trash")}
                                    >
                                        <i className="fas fa-trash-alt" />
                                        <span>Trash</span>
                                    </button>
                                ) : (
                                    <>
                                        <button
                                            className="slv-btn success"
                                            onClick={onActionRestoreClick}
                                        >
                                            <i className="fas fa-trash-restore" />
                                            <span>Restore</span>
                                        </button>

                                        <button
                                            className="slv-btn danger"
                                            onClick={() => onActionTrashClick("Delete")}
                                        >
                                            <i className="fas fa-trash-alt" />
                                            <span>Delete</span>
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="slv-toolbar-right">
                        {/* Create folder */}
                        <OverlayTrigger
                            show={createFoldPop}
                            trigger="hover"
                            placement="left"
                            overlay={
                                <Popover className="slv-create-fold-pop"
                                    onMouseEnter={() => setCreateFoldPop(true)}
                                    onMouseLeave={() => setCreateFoldPop(false)}>
                                    <PopoverHeader>New folder</PopoverHeader>
                                    <PopoverBody>
                                        <InputGroup size="sm">
                                            <FormControl
                                                placeholder="Folder name"
                                                value={folderToBeCreate}
                                                onChange={(e) => setFolderToBeCreate(e.target.value.replace(/[^\w ]/g, "").replace(/^ +/, ""))}
                                            />
                                            <Button variant="dark" onClick={onCreateFolderClick}>Create</Button>
                                        </InputGroup>
                                    </PopoverBody>
                                </Popover>
                            }>
                            <button
                                type="button"
                                className="slv-tbtn amber"
                                onMouseEnter={() => setCreateFoldPop(true)}
                                onMouseLeave={() => setCreateFoldPop(false)}
                                aria-label="Create folder"
                                title="New folder"
                                disabled={props.trashFileFlag}
                            >
                                <i className="fas fa-folder-plus" />
                                <span>New folder</span>
                            </button>
                        </OverlayTrigger>

                        {/* Upload (single dropdown) */}
                        <OverlayTrigger placement="bottom" overlay={<Tooltip id="tt-upload">Upload</Tooltip>}>
                            <Dropdown className="slv-upload-dd">
                                <DropdownToggle disabled={props.trashFileFlag} aria-label="Upload">
                                    <i className="fa-solid fa-cloud-arrow-up" />
                                    <span>Upload</span>
                                </DropdownToggle>
                                <DropdownMenu align="end">
                                    <DropdownItem onClick={() => setShowUploadModel(true)}>
                                        <i className="fa-solid fa-file-lines" style={{ color: "#2563eb", width: 16 }} />
                                        <span>Upload files</span>
                                    </DropdownItem>
                                    <DropdownItem onClick={showFolderUpload}>
                                        <i className="fa-solid fa-folder" style={{ color: "#d97706", width: 16 }} />
                                        <span>Upload folder</span>
                                    </DropdownItem>
                                </DropdownMenu>
                            </Dropdown>
                        </OverlayTrigger>
                        {/* Selected files badge */}
                        {selectionCount > 0 && (
                            <OverlayTrigger placement="left" overlay={<Tooltip id="tt-sel">View selection</Tooltip>}>
                                <button type="button" className="slv-icon-btn blue" onClick={() => setOpenSelectedFilesDrawer(true)}
                                    aria-label="View selected items">
                                    <i className="fa-solid fa-layer-group" />
                                </button>
                            </OverlayTrigger>
                        )}

                        <input type="file" webkitdirectory="" multiple="" directory="" ref={folderUploadRef}
                            hidden onChange={handleFolderUploadFiles} onClick={(e) => (e.target.value = null)} />
                    </div>
                </div>

                {/* ── Path breadcrumb ── */}
                {storageNavigations && (
                    <div className="slv-pathbar" aria-label="Current folder path">
                        {storageNavigations}
                    </div>
                )}

                {/* ── Column headers (list view) ── */}
                {props.viewType === 1 && (
                    <div className="slv-head">
                        <div className="slv-head-name">
                            <FormCheck
                                checked={selectAllFlag}
                                onChange={onSelectAll}
                                aria-label="Select all"
                            />
                            <span
                                className="slv-head-cell"
                                onClick={() => { const t = getSortType === "desc" ? "asc" : "desc"; setSortType(t); clickSorting(t, "name"); }}
                                title="Sort by name"
                            >
                                Name <i className={`fas fa-sort-${getSortType === "asc" ? "up" : "down"}`} style={{ fontSize: 9 }} />
                            </span>
                        </div>
                        <span
                            className="slv-head-cell"
                            onClick={() => { const t = getSortType === "desc" ? "asc" : "desc"; setSortType(t); clickSorting(t, "size"); }}
                            title="Sort by size"
                        >
                            Size
                        </span>
                        <span
                            className="slv-head-cell"
                            style={{ justifyContent: "flex-end" }}
                            onClick={() => { const t = getSortType === "desc" ? "asc" : "desc"; setSortType(t); clickSorting(t, "created"); }}
                            title="Sort by date"
                        >
                            Modified
                        </span>
                    </div>
                )}

                {/* ── List view ── */}
                {props.viewType === 1 && (
                    <Scrollbars
                        style={{ flex: 1 }}
                        onScroll={handleScroll}
                    >
                        {showLoading && (
                            <div className="slv-loading" aria-label="Loading">
                                <ClipLoader color="#2563eb" size={26} />
                            </div>
                        )}
                        {!showLoading && getstorageFolders.length + getstorageFiles.length === 0 && (
                            <SAEmpty message={props.trashFileFlag ? "Trash is empty" : "No files or folders"} center />
                        )}

                        {/* Folders */}
                        {!showLoading && getstorageFolders.length > 0 && (
                            <div className="slv-list-body">
                                {getstorageFolders.map((item, index) => {
                                    const folderSelected = storageFoldersToBeAdded.some(
                                        (f) => (f.storageId === props.storageId || f.storageId === null) && f.filePath === item
                                    );
                                    return (
                                        <div key={item || index} className={`slv-row${folderSelected ? " selected" : ""}`}>
                                            <div className="slv-row-name">
                                                {props.showSelectBox && (
                                                    <i
                                                        className={`slv-checkbox ${folderSelected ? "fas fa-check-square" : "far fa-square"}`}
                                                        data-type="folder"
                                                        data-name={item}
                                                        data-full_path={item}
                                                        data-checked={folderSelected ? "1" : "0"}
                                                        onClick={fileCheckClicked}
                                                    />
                                                )}
                                                <span className="slv-file-icon" aria-hidden="true">
                                                    <i className="fas fa-folder" style={{ color: "#d97706" }} />
                                                </span>
                                                <span className="slv-row-label">
                                                    <span className="name folder-name" onClick={() => onFolderClick(item)} title={item}>
                                                        {trimFolderName(item)}
                                                    </span>
                                                </span>
                                            </div>
                                            <span className="slv-row-size">—</span>
                                            <div className="slv-row-date">
                                                <span>—</span>
                                                <button type="button" className="slv-ctx-btn" onClick={(e) => openActionMenu(e, item)} aria-label="Actions">
                                                    <i className="fa-solid fa-ellipsis-vertical" />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Files */}
                        {!showLoading && getstorageFiles.length > 0 && (
                            <div className="slv-list-body">
                                {getstorageFiles.map((file, ind) => {
                                    const fileSelected = storageFilesToBeAdded.some(
                                        (f) => (f.storageId === props.storageId || f.storageId === null) && f.filePath === file.name
                                    );
                                    return (
                                        <div key={file.name || ind} className={`slv-row${fileSelected ? " selected" : ""}`}>
                                            <div className="slv-row-name">
                                                {props.showSelectBox && (
                                                    <i
                                                        className={`slv-checkbox ${fileSelected ? "fas fa-check-square" : "far fa-square"}`}
                                                        data-type="file"
                                                        data-name={file.name}
                                                        data-full_path={file.full_path}
                                                        data-checked={fileSelected ? "1" : "0"}
                                                        onClick={fileCheckClicked}
                                                    />
                                                )}
                                                <span className="slv-file-icon" aria-hidden="true">
                                                    <img src={findIcon(imageFileExtension(file.name))} alt="" />
                                                </span>
                                                <span className="slv-row-label">
                                                    <span className="name" title={file.name}>{trimFileName(file.name)}</span>
                                                </span>
                                            </div>
                                            <span className="slv-row-size">{fileSizeSI(file.size)}</span>
                                            <div className="slv-row-date">
                                                <span title={Moment(file.created).format("MMM DD, YYYY hh:mm A")}>
                                                    {Moment(file.created).format("MMM DD, YYYY")}
                                                </span>
                                                <button type="button" className="slv-ctx-btn" onClick={(e) => openActionMenu(e, file)} aria-label="Actions">
                                                    <i className="fa-solid fa-ellipsis-vertical" />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {loadMoreFlag && !showLoading && (
                            <div className="slv-loading">
                                <FadeLoader color="#111110" height={10} width={2} margin={2} />
                            </div>
                        )}
                        {!showLoading && getnextPageToken === null && (
                            <div className="slv-page-end">— End of list —</div>
                        )}
                    </Scrollbars>
                )}

                {/* ── Grid view ── */}
                {props.viewType === 2 && (
                    <Scrollbars
                        style={{ flex: 1 }}
                        onScroll={handleScroll}
                    >
                        {showLoading && (
                            <div className="slv-loading" aria-label="Loading">
                                <ClipLoader color="#2563eb" size={26} />
                            </div>
                        )}
                        {!showLoading && getstorageFolders.length + getstorageFiles.length === 0 && (
                            <SAEmpty message={props.trashFileFlag ? "Trash is empty" : "No files or folders"} center />
                        )}
                        {!showLoading && <div className="slv-grid">
                            {getstorageFolders.map((item, index) => {
                                const folderSelected = storageFoldersToBeAdded.some(
                                    (f) => (f.storageId === props.storageId || f.storageId === null) && f.filePath === item
                                );
                                return (
                                    <div key={item || index} className={`slv-card${folderSelected ? " selected" : ""}`}
                                        onClick={() => onFolderClick(item)} role="button" tabIndex={0}
                                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onFolderClick(item); } }}>
                                        {props.showSelectBox && (
                                            <span className="slv-card-check"
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); fileCheckClicked({ preventDefault: () => {}, stopPropagation: () => {}, target: { dataset: { type: "folder", name: item, full_path: item, checked: folderSelected ? "1" : "0" }, className: "" } }); }}
                                                role="button" tabIndex={0} aria-label={folderSelected ? "Unselect" : "Select"}>
                                                <i className={folderSelected ? "fas fa-check-square" : "far fa-square"} />
                                            </span>
                                        )}
                                        <div className="slv-card-thumb">
                                            <i className="fas fa-folder" style={{ color: "#d97706" }} />
                                            <button type="button" className="slv-card-ctx" onClick={(e) => { e.stopPropagation(); openActionMenu(e, item); }} aria-label="Actions">
                                                <i className="fa-solid fa-ellipsis-vertical" />
                                            </button>
                                        </div>
                                        <div className="slv-card-body">
                                            <div className="slv-card-name" title={item}>{trimFolderName(item)}</div>
                                            <div className="slv-card-meta"><span>Folder</span></div>
                                        </div>
                                    </div>
                                );
                            })}
                            {getstorageFiles.map((file, ind) => {
                                const fileSelected = storageFilesToBeAdded.some(
                                    (f) => (f.storageId === props.storageId || f.storageId === null) && f.filePath === file.name
                                );
                                return (
                                    <div key={file.name || ind} className={`slv-card${fileSelected ? " selected" : ""}`}>
                                        {props.showSelectBox && (
                                            <span className="slv-card-check"
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); fileCheckClicked({ preventDefault: () => {}, stopPropagation: () => {}, target: { dataset: { type: "file", name: file.name, full_path: file.full_path, checked: fileSelected ? "1" : "0" }, className: "" } }); }}
                                                role="button" tabIndex={0} aria-label={fileSelected ? "Unselect" : "Select"}>
                                                <i className={fileSelected ? "fas fa-check-square" : "far fa-square"} />
                                            </span>
                                        )}
                                        <div className="slv-card-thumb">
                                            <img src={findIcon(imageFileExtension(file.name))} alt="" />
                                            <button type="button" className="slv-card-ctx" onClick={(e) => openActionMenu(e, file)} aria-label="Actions">
                                                <i className="fa-solid fa-ellipsis-vertical" />
                                            </button>
                                        </div>
                                        <div className="slv-card-body">
                                            <div className="slv-card-name" title={file.name}>{trimFileName(file.name)}</div>
                                            <div className="slv-card-meta">
                                                <span>{fileSizeSI(file.size)}</span>
                                                <span>{Moment(file.created).format("MMM DD")}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>}
                        {!showLoading && getnextPageToken === null && (
                            <div className="slv-page-end">— End of list —</div>
                        )}
                    </Scrollbars>
                )}
            </div>

            {/* ── Action popover ── */}
            <Overlay
                show={actionMenu.show}
                target={actionMenu.target}
                placement="left-start"
                container={document.body}
                rootClose
                onHide={closeActionMenu}
            >
                {(overlayProps) => (
                    <Popover {...overlayProps} className="slv-popover" style={{ ...overlayProps.style, zIndex: 10000 }}>
                        <PopoverBody>
                            {(buildActionEntries(actionMenu.item) || []).map((entry, idx) => {
                                if (entry.divider) return <div key={`d-${idx}`} className="slv-pop-divider" />;
                                return (
                                    <button key={`${entry.label}-${idx}`} type="button" className="slv-pop-item"
                                        onClick={() => { closeActionMenu(); entry.onClick?.(); }}>
                                        <span className={`slv-pop-ico ${entry.icoClass || "blue"}`}>
                                            <i className={entry.icon} />
                                        </span>
                                        <span>{entry.label}</span>
                                    </button>
                                );
                            })}
                        </PopoverBody>
                    </Popover>
                )}
            </Overlay>

            {/* ── Modals ── */}
            {/* Delete / Trash */}
            <Modal backdrop="static" show={trashMod} onHide={() => setTrashMod(false)} className="slv-modal" centered>
                <ModalHeader closeButton>
                    <span>{deleteType === "Trash" ? "Move to trash" : "Delete permanently"}</span>
                </ModalHeader>
                <ModalBody>
                    <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>
                        {deleteType === "Trash"
                            ? "Are you sure you want to move the selected items to trash?"
                            : "This action cannot be undone. Are you sure you want to permanently delete the selected items?"}
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button variant="light" size="sm" onClick={() => setTrashMod(false)}>Cancel</Button>
                    <Button variant="danger" size="sm" onClick={handleTrashDelete}>
                        {deleteType === "Trash" ? "Move to trash" : "Delete permanently"}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Restore */}
            <Modal backdrop="static" show={restoreMod} onHide={() => setRestoreMod(false)} className="slv-modal" centered>
                <ModalHeader closeButton><span>Restore</span></ModalHeader>
                <ModalBody>
                    <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>
                        Are you sure you want to restore the selected items?
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button variant="light" size="sm" onClick={() => setRestoreMod(false)}>Cancel</Button>
                    <Button variant="dark" size="sm" onClick={handleRestore}>Restore</Button>
                </ModalFooter>
            </Modal>

            {/* Selected Files Drawer */}
            <Offcanvas placement="end" show={openSelectedFilesDrawer} onHide={() => setOpenSelectedFilesDrawer(false)} backdrop="static">
                <OffcanvasHeader closeButton>
                    <OffcanvasTitle>Selected items</OffcanvasTitle>
                </OffcanvasHeader>
                <OffcanvasBody>
                    <SelectedInfo
                        storageData={[...storageFoldersToBeAdded, ...storageFilesToBeAdded]}
                        _removeSingleItem={_removeSingleItem}
                        _clearAllSeleted={_clearAllSeleted}
                        _cancelDrawer={() => setOpenSelectedFilesDrawer(false)}
                    />
                </OffcanvasBody>
            </Offcanvas>

            {/* File Upload */}
            <Modal className="slv-modal" show={showUploadModel} onHide={() => { setShowUploadModel(false); onRefresh(); }} backdrop="static" size="lg" centered>
                <ModalHeader closeButton>Upload files</ModalHeader>
                <ModalBody>
                    <DropFileInput uploadType="general" prefix={generatePrefix()} storageId={null} rootFolder={null}
                        saveVersion={false} closeModal={() => setShowUploadModel(false)} />
                </ModalBody>
            </Modal>

            {/* Folder Upload */}
            <Modal className="slv-modal" show={showFolderUploadModel} onHide={() => { setShowFolderUploadModel(false); onRefresh(); }} backdrop="static" size="lg" centered>
                <ModalHeader closeButton>Upload folder</ModalHeader>
                <ModalBody>
                    <DropFileInput uploadType="general" prefix={generatePrefix()} storageId={null} rootFolder={null}
                        saveVersion={false} closeModal={() => setShowFolderUploadModel(false)} />
                </ModalBody>
            </Modal>

            {/* Rename */}
            <Modal className="slv-modal" show={showRenameModal} onHide={toggleRenameModal} centered>
                <ModalHeader closeButton>Rename</ModalHeader>
                <ModalBody>
                    <input
                        placeholder="New name"
                        className="form-control"
                        value={renameToBeCreate}
                        autoComplete="off"
                        onChange={handleRenameChange}
                        style={{ fontSize: 13 }}
                    />
                    <p style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
                        <strong>Note:</strong> Update any project references after renaming.
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button variant="light" size="sm" onClick={() => setShowRenameModal(false)}>Cancel</Button>
                    <Button variant="dark" size="sm" onClick={renameUpdate}>Rename</Button>
                </ModalFooter>
            </Modal>

            {/* Copy / Move */}
            <Modal className="slv-modal" dialogClassName="slv-op-dialog" show={showCopyMoveModal} backdrop="static"
                onHide={() => setShowCopyMoveModal(false)} size="xl" centered>
                <ModalHeader closeButton>
                    <span>{modeTransfer} to</span>
                </ModalHeader>
                <ModalBody>
                    <StorageFiles _getSelectedFiles={_getSelectedFiles} _getStorageDetail={_getStorageDetail}
                        showSelectBox={false} buttonText={modeTransfer} selectFromLocation="operation" actionFooter />
                </ModalBody>
            </Modal>

            {/* Confirm folder navigation */}
            <Modal className="slv-modal" show={confirmFldMod} onHide={() => setConfirmFldMod(false)} size="sm" centered>
                <ModalHeader closeButton>Selection will be cleared</ModalHeader>
                <ModalBody>
                    <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>
                        You have items selected. Navigating to a different folder will clear your selection. Continue?
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button variant="light" size="sm" onClick={() => setConfirmFldMod(false)}>Stay</Button>
                    <Button variant="dark" size="sm" onClick={() => { setConfirmFldMod(false); _clearAllSeleted(); folderClickPath(getCurrentFolder); }}>
                        Continue
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default StorageListView;